/**
 * Point d'entrée unique : connexion ET création d'entreprise.
 *
 * Les deux intentions vivent sur le même écran et basculent sur place, sans
 * navigation. L'utilisateur DÉCLARE laquelle il veut : on n'interroge jamais le
 * serveur pour savoir si un identifiant existe, ce qui reviendrait à publier la
 * liste des comptes MyEquip. L'onglet de création n'apparaît que si l'API dit
 * l'inscription ouverte — c'est-à-dire seulement avec des conditions générales
 * publiées.
 *
 * Connexion et enrôlement MFA (Q50, ADR-005) :
 * - mot de passe → MFA obligatoire selon les droits (`mfa-policy.ts`) ;
 * - 1re connexion : QR TOTP RÉEL scannable + clé manuelle copiable ;
 * - codes de secours BLOQUANTS : copie/téléchargement + confirmation explicite
 *   avant d'entrer dans l'application (le profil n'est rechargé qu'après —
 *   sinon le routeur démonterait cet écran et perdrait les codes) ;
 * - clé d'accès (décision 30) : bouton « Se connecter avec Face ID ou
 *   l'empreinte » et suggestion discrète dans le champ identifiant quand le
 *   navigateur sait la proposer ; la clé vaut double authentification ;
 * - comme les grands services (24/10/2026, issue #138) : l'identifiant d'abord,
 *   puis le mot de passe ; l'appareil qui a servi avec une clé d'accès propose
 *   directement « Continuer avec Face ID », le mot de passe en second ;
 * - mise en place imposée : Face ID d'abord, l'appli de codes repliée, le code
 *   par e-mail pour les comptes sans droit sensible (le serveur décide) ;
 * - étape « Code de vérification » : une ligne d'aide, le détail replié, et
 *   « Autre compte » pour revenir à l'identifiant.
 *
 * Compte personnel à plusieurs entreprises (25/10/2026) : le serveur entre dans
 * la seule ou la dernière utilisée ; sinon la personne choisit parmi les noms
 * de ses entreprises (`choose_company`). Sans aucune entreprise en service
 * (`no_company`) : ses invitations en attente, et l'enregistrement de sa propre
 * entreprise avec ce compte. Une invitation acceptée avec un compte existant
 * (`InvitationPage`) fait passer par ici, puis y ramène.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, KeyRound, Mail, Smartphone, UserRound } from 'lucide-react';
import { useAuth, type LoginStep } from '../../core/auth.js';
import { dateLongue } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { invitationPath, readPendingInvitation, writePendingInvitation } from '../../core/memberships.js';
import { ICONS } from '../../design/icons.js';
import { Button, Field, Input, Spinner, Tabs } from '../../components/ui.js';
import { RecordIcon, RecordList, RecordRow } from '../../components/RecordRow.js';
import { Logo } from '../../components/Logo.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Section } from '../../components/ui/Section.js';
import { CheckboxGroup } from '../../components/ui/ChoiceGroup.js';
import { Switch } from '../../components/ui/Switch.js';
import { ButtonLink } from '../../components/ui/action.js';
import { ApiError, api, clearSessionEndNotice, isEmailUnproven, sessionEndNotice } from '../../core/api.js';
import { BackupCodesGate, MfaSetupPanel } from '../../components/auth/MfaSetup.js';
import {
  cancelPasskeyRequest, passkeyAutofillSupported, passkeyCancelled, passkeyErrorMessage, passkeyMethodName, passkeysSupported,
  platformPasskeyAvailable,
} from '../../core/passkeys.js';
import { forgetRememberedAccount, rememberedAccount } from '../../core/remembered-account.js';
import { EmailCodeStep } from './EmailCodeStep.js';
import { AdvisorPendingSection, DeleteCompanylessAccount } from './NoCompanyAccount.js';
import { LOGIN_NOTICE_PARAM, LOGIN_NOTICES } from '../profile/account-closure.js';
import { LoginBackdrop, loginBackdropEnabled } from './LoginBackdrop.js';
import './auth.css';
import { Banner } from '../../components/ui/Feedback.js';
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '@myequip/shared';

/** Un défi de clé d'accès vit 5 minutes : la suggestion discrète est renouvelée avant. */
const AUTOFILL_RENEW_MS = 4 * 60 * 1000;

/** Erreurs annoncées par un retour vers l'écran de connexion (`?erreur=`). */
const RETURN_ERRORS = ['mfa_recuperation'] as const;

export function returnedError(value: string | null): string | undefined {
  return (RETURN_ERRORS as readonly string[]).includes(value ?? '') ? t(`auth.erreurs.${value}`) : undefined;
}

/**
 * Fin de session annoncée : voulue (compte supprimé, entreprise quittée, `?retour=`),
 * ou subie au renouvellement (appareil retiré, double authentification exigée, `api.ts`).
 */
function returnedNotice(value: string | null | undefined): string | undefined {
  return (LOGIN_NOTICES as readonly string[]).includes(value ?? '') ? t(`auth.retours.${value}`) : undefined;
}

interface LegalDocument {
  id: string;
  kind: string;
  version: string;
  title: string;
  url: string;
}

interface RegistrationInfo {
  registrationOpen: boolean;
  legalDocuments: LegalDocument[];
}

/** Documents publiés à lire et accepter avant d'enregistrer une entreprise. */
function LegalConsent({ documents, accepted, onChange }: {
  documents: LegalDocument[];
  accepted: string[];
  onChange: (values: string[]) => void;
}): ReactNode {
  if (!documents.length) return null;
  return (
    <>
      <CheckboxGroup
        label={t('auth.documentsAAccepter')}
        values={accepted}
        onChange={onChange}
        options={documents.map((document) => ({
          value: document.id,
          label: t('auth.inscriptionAccepter', { document: `${document.title} (${document.version})` }),
        }))}
      />
      <div className="login-page__links">
        {documents.map((document) => (
          <ButtonLink key={document.id} href={document.url} variant="ghost">
            {t('auth.lireDocument', { document: document.title })}
          </ButtonLink>
        ))}
      </div>
    </>
  );
}

/**
 * Création d'entreprise. Le formulaire ne demande que ce qu'un client peut
 * légitimement décider : le plan, l'identifiant et le rôle sont posés par le
 * serveur. Le reste du paramétrage attend l'entrée dans le produit.
 */
function RegisterForm({ info, onRegistered }: {
  info: RegistrationInfo;
  onRegistered: (email: string, password: string) => Promise<void>;
}): ReactNode {
  const [companyName, setCompanyName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accepted, setAccepted] = useState<string[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const tousAcceptes = info.legalDocuments.every((d) => accepted.includes(d.id));

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await api('/auth/register', {
        method: 'POST',
        body: {
          companyName, fullName, email, password,
          acceptedLegalDocumentIds: accepted,
        },
      });
      // L'API répond la même chose si l'adresse est déjà connue : on enchaîne
      // donc sur une connexion, qui échouera normalement dans ce cas.
      await onRegistered(email, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('commun.erreurInattendue'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="stack">
      <p className="muted">{t('auth.inscriptionRole')}</p>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Field label={t('auth.inscriptionSociete')}>
        <Input value={companyName} onChange={setCompanyName} required
          autoComplete="organization" />
      </Field>
      <Field label={t('auth.inscriptionNom')}>
        <Input value={fullName} onChange={setFullName} required autoComplete="name" />
      </Field>
      <Field label={t('auth.email')}>
        <Input value={email} onChange={setEmail} type="email" required autoComplete="email" />
      </Field>
      <Field label={t('auth.motDePasse')} hint={t('auth.inscriptionMotDePasseAide', { n: PASSWORD_MIN_LENGTH })}>
        <Input value={password} onChange={setPassword} type="password" maxLength={PASSWORD_MAX_LENGTH} required
          autoComplete="new-password" />
      </Field>
      <LegalConsent documents={info.legalDocuments} accepted={accepted} onChange={setAccepted} />
      <Button type="submit" large disabled={busy || !tousAcceptes}>
        {t('auth.inscriptionValider')}
      </Button>
    </form>
  );
}

/**
 * Entreprise enregistrée par un compte prouvé sans entreprise (25/10/2026) :
 * son nom et les conditions ; la personne, son adresse et son mot de passe
 * sont ceux du compte. Le jeton de compte ne sert qu'une fois.
 */
function AccountCompanyForm({ info, accountToken, onDone, onExpired }: {
  info: RegistrationInfo;
  accountToken: string;
  onDone: (response: { status?: 'ok'; accessToken?: string; refreshToken?: string; signInRequired?: boolean }) => Promise<void>;
  onExpired: (message: string) => void;
}): ReactNode {
  const [companyName, setCompanyName] = useState('');
  const [accepted, setAccepted] = useState<string[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [unproven, setUnproven] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = companyName.trim().length > 0 && info.legalDocuments.every((d) => accepted.includes(d.id));

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await onDone(await api('/auth/account/companies', {
        method: 'POST',
        body: { companyName: companyName.trim(), acceptedLegalDocumentIds: accepted, accountToken },
      }));
    } catch (err) {
      // Jeton de compte déjà consommé ou échu : il faut se reconnecter.
      if (err instanceof ApiError && err.code === 'unauthorized') onExpired(err.message);
      else {
        setUnproven(isEmailUnproven(err));
        setError(err instanceof ApiError ? err.message : t('commun.erreurInattendue'));
      }
      setBusy(false);
    }
  };

  return (
    <Section title={t('compteEntreprises.creer')} hint={t('compteEntreprises.creerAide')} flush>
      <form onSubmit={(e) => void onSubmit(e)} className="stack">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {unproven ? <Link to="/reinitialisation">{t('compteEntreprises.prouverAdresse')}</Link> : null}
        <Field label={t('compteEntreprises.creerNom')}>
          <Input value={companyName} onChange={setCompanyName} required autoComplete="organization" />
        </Field>
        <LegalConsent documents={info.legalDocuments} accepted={accepted} onChange={setAccepted} />
        <Button type="submit" large disabled={busy || !ready}>{t('compteEntreprises.creer')}</Button>
      </form>
    </Section>
  );
}

export function LoginPage(): ReactNode {
  const {
    login, loginWithPasskey, verifyMfa, setupMfa, activateMfa, activateMfaWithPasskey, finishLogin, chooseCompany,
  } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Invitation à accepter avec ce compte une fois connecté (`InvitationPage`).
  const [pendingInvitation, setPendingInvitation] = useState(readPendingInvitation);
  // L'intention vient de l'utilisateur (onglet, ou lien depuis la vitrine),
  // jamais d'une vérification serveur sur l'identifiant saisi.
  const [intent, setIntent] = useState<'connexion' | 'inscription'>(
    // ui-allow ui/url-param-names: lien public de la vitrine (apps/marketing), adresse publiée
    params.get('intent') === 'inscription' ? 'inscription' : 'connexion',
  );
  const [registration, setRegistration] = useState<RegistrationInfo | undefined>();
  const [passkeys] = useState(passkeysSupported);
  // Lecteur intégré (Face ID, Touch ID, empreinte, Windows Hello) : il donne son nom à la
  // clé d'accès et décide de la méthode mise en avant ; sans lui, rien ne promet Face ID.
  const [platform, setPlatform] = useState(false);
  useEffect(() => { void platformPasskeyAvailable().then(setPlatform); }, []);
  const methode = passkeyMethodName(platform);
  // Mise en place imposée : le choix de la méthode, ou l'appli de codes ouverte.
  const [setupMethod, setSetupMethod] = useState<'choix' | 'appli'>('choix');
  // Compte retenu par cet appareil : sa clé d'accès d'abord, comme les grands services.
  const [remembered, setRemembered] = useState(rememberedAccount);
  // ui-allow ui/url-param-names: identifiant prérempli par la page d'invitation, déjà dans les e-mails
  const invited = params.get('identifiant');
  const [identifier, setIdentifier] = useState(invited ?? remembered?.identifier ?? '');
  const [phase, setPhase] = useState<'compte' | 'identifiant' | 'motDePasse'>(
    // Arrivée d'une invitation acceptée : l'identifiant est connu, le mot de passe tout juste choisi.
    () => (invited ? 'motDePasse' : remembered?.passkey && passkeys ? 'compte' : 'identifiant'),
  );
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  // Coché par défaut (issue #138) : sur son propre téléphone, plus de code pendant 30 jours.
  const [rememberDevice, setRememberDevice] = useState(true);
  const [step, setStep] = useState<LoginStep | undefined>();
  // Code par e-mail choisi pour finir cette étape (jeton de l'étape en cours).
  const [emailToken, setEmailToken] = useState<string | undefined>();
  const [setup, setSetup] = useState<{ otpauthUri: string; secret: string } | undefined>();
  const [backupCodes, setBackupCodes] = useState<string[] | undefined>();
  // Fin de connexion tenue jusqu'à ce que les codes de secours soient gardés.
  const afterCodes = useRef<Parameters<typeof finishLogin>[0] | undefined>(undefined);
  // ui-allow ui/url-param-names: retour d'un parcours serveur (récupération du compte) vers la connexion
  const [error, setError] = useState<string | undefined>(() => returnedError(params.get('erreur')));
  // Information qui suit un retour à l'identifiant (entreprise enregistrée, reconnexion attendue).
  const [notice, setNotice] = useState<string | undefined>(() => returnedNotice(sessionEndNotice()));
  // Lu à chaque rendu : l'écran est souvent déjà affiché quand la fin de session y ramène.
  // ui-allow ui/url-param-names: retour d'une fin de session voulue (compte supprimé, entreprise quittée)
  const returned = returnedNotice(params.get(LOGIN_NOTICE_PARAM));
  // Dite une fois : une connexion suivante dans cet onglet ne la répète pas.
  useEffect(() => clearSessionEndNotice(), []);
  const [busy, setBusy] = useState(false);
  const [backdrop] = useState(loginBackdropEnabled);
  const [autofill, setAutofill] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // Garde synchrone : un double appui ne lance qu'une cérémonie.
  const passkeyPending = useRef(false);

  useEffect(() => {
    void api<RegistrationInfo>('/auth/register/info')
      .then(setRegistration)
      // Inscription fermée ou injoignable : l'écran reste une page de connexion.
      .catch(() => setRegistration({ registrationOpen: false, legalDocuments: [] }));
  }, []);

  useEffect(() => {
    let alive = true;
    void passkeyAutofillSupported().then((supported) => { if (alive) setAutofill(supported); });
    return () => { alive = false; };
  }, []);

  // Compte reconnu pendant l'acceptation d'une invitation : retour à sa page, avec la
  // session ouverte ou le jeton de compte (en mémoire de l'historique, jamais dans l'adresse).
  useEffect(() => {
    if (step?.status !== 'invitation') return;
    writePendingInvitation(undefined);
    navigate(invitationPath(step.token), {
      replace: true, ...(step.accountToken ? { state: { accountToken: step.accountToken } } : {}),
    });
  }, [step, navigate]);

  const signingIn = !step || step.status === 'ok';
  const autofillActive = autofill && signingIn && intent === 'connexion' && phase === 'identifiant' && !passkeyBusy;
  // Suggestion discrète : la clé choisie dans le champ identifiant connecte
  // directement. Renouvelée avant l'échéance du défi ; une nouvelle demande
  // (bouton) annule la précédente.
  useEffect(() => {
    if (!autofillActive) return undefined;
    let alive = true;
    const listen = (): void => {
      loginWithPasskey(true).then((next) => { if (alive) setStep(next); }).catch((err: unknown) => {
        if (alive && !passkeyCancelled(err)) setError(passkeyErrorMessage(err));
      });
    };
    listen();
    const timer = window.setInterval(listen, AUTOFILL_RENEW_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
      cancelPasskeyRequest();
    };
  }, [autofillActive, loginWithPasskey]);

  const ceremony = async (run: () => Promise<void>): Promise<void> => {
    if (passkeyPending.current) return;
    passkeyPending.current = true;
    setError(undefined);
    setPasskeyBusy(true);
    try {
      await run();
    } catch (err) {
      if (!passkeyCancelled(err)) setError(passkeyErrorMessage(err));
    } finally {
      passkeyPending.current = false;
      setPasskeyBusy(false);
    }
  };
  const onPasskey = (): Promise<void> => ceremony(async () => setStep(await loginWithPasskey()));
  const onSetupPasskey = (setupToken: string): Promise<void> =>
    ceremony(async () => setStep(await activateMfaWithPasskey(setupToken)));

  const autreCompte = (): void => {
    setNotice(undefined);
    setStep(undefined);
    setSetup(undefined);
    setEmailToken(undefined);
    setSetupMethod('choix');
    setCode('');
    setPassword('');
    setRememberDevice(true);
    setError(undefined);
    setPhase('identifiant');
  };
  const oublierCompte = (): void => {
    forgetRememberedAccount();
    setRemembered(undefined);
    setIdentifier('');
    autreCompte();
  };

  const connecter = async (id: string, motDePasse: string): Promise<void> => {
    const result = await login(id, motDePasse);
    setStep(result);
    // Mise en place sans autre méthode possible que l'appli de codes : elle s'ouvre d'emblée.
    if (result.status === 'mfa_setup_required' && !passkeys && !result.emailCode) {
      setSetupMethod('appli');
      setSetup(await setupMfa(result.setupToken));
    }
    // Le code par e-mail est la méthode choisie : il part tout de suite.
    if (result.status === 'mfa_required' && result.method === 'email') setEmailToken(result.mfaToken);
  };

  const onIdentifier = (e: FormEvent): void => {
    e.preventDefault();
    setError(undefined);
    if (identifier.trim()) setPhase('motDePasse');
  };

  const onLogin = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await connecter(identifier, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('commun.erreurInattendue'));
    } finally {
      setBusy(false);
    }
  };

  const onShowTotpSetup = async (setupToken: string): Promise<void> => {
    if (setup) return;
    try {
      setSetup(await setupMfa(setupToken));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('commun.erreurInattendue'));
    }
  };

  const onMfa = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      if (step?.status === 'mfa_required') {
        setStep(await verifyMfa(step.mfaToken, code.trim(), rememberDevice));
      } else if (step?.status === 'mfa_setup_required') {
        const activated = await activateMfa(step.setupToken, code);
        afterCodes.current = activated.next;
        setBackupCodes(activated.backupCodes);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('commun.erreurInattendue'));
    } finally {
      setBusy(false);
    }
  };

  /** Le jeton de compte a servi ou expiré : retour au mot de passe, identifiant gardé. */
  const reconnect = (message: string | undefined, info?: string): void => {
    autreCompte();
    setPhase(identifier ? 'motDePasse' : 'identifiant');
    setError(message);
    setNotice(info);
  };

  const onChoose = async (accountToken: string, userId: string): Promise<void> => {
    setError(undefined);
    setBusy(true);
    try {
      setStep(await chooseCompany(accountToken, userId));
    } catch (err) {
      // Le jeton ne sert qu'une fois, même refusé : la connexion recommence.
      reconnect(err instanceof ApiError && err.code !== 'network_error'
        ? err.message : t('compteEntreprises.connexionExpiree'));
    } finally {
      setBusy(false);
    }
  };

  const onCompanyCreated = async (
    response: { status?: 'ok'; accessToken?: string; refreshToken?: string; signInRequired?: boolean },
  ): Promise<void> => {
    if (response.signInRequired || !response.accessToken) {
      reconnect(undefined, t('compteEntreprises.creee'));
      return;
    }
    setStep(await finishLogin(response));
  };

  const passkeyButton = (label: string, onClick: () => void, variant?: 'secondary'): ReactNode => (
    <Button large {...(variant ? { variant } : {})} disabled={busy || passkeyBusy} onClick={onClick}>
      <KeyRound size={18} aria-hidden="true" />
      {passkeyBusy ? t('auth.passkeyEnCours') : label}
    </Button>
  );
  const backButton = (onClick: () => void = autreCompte): ReactNode => (
    <Button variant="ghost" disabled={busy || passkeyBusy} onClick={onClick}>
      <ArrowLeft size={18} aria-hidden="true" />
      {t('commun.retour')}
    </Button>
  );
  const chooseApp = (setupToken: string): void => {
    setSetupMethod('appli');
    void onShowTotpSetup(setupToken);
  };

  /** Code TOTP : saisie, appareil de confiance et validation (connexion comme mise en place). */
  const totpForm = (current: Extract<LoginStep, { status: 'mfa_required' | 'mfa_setup_required' }>): ReactNode => (
    // Un code à usage unique n'est pas un mot de passe : rien à enregistrer pour les gestionnaires.
    <form onSubmit={(e) => void onMfa(e)} className="stack" autoComplete="off">
      {current.status === 'mfa_setup_required' ? (
        setup ? <MfaSetupPanel setup={setup} /> : <Spinner variant="section" label={t('auth.mfaAvecAppli')} />
      ) : (
        <>
          <p className="muted">{t('auth.codeMfaAide')}</p>
          <Section title={t('auth.codeMfaOu')} collapsible flush>
            <p className="muted">{t('auth.codeMfaDetail')}</p>
          </Section>
        </>
      )}
      {/* Un code de secours fait 10 caractères : au-delà, c'est presque
          toujours le mot de passe tapé au mauvais endroit. */}
      <Field label={t('auth.codeMfa')}
        error={code.trim().length > 10 ? t('auth.codeRessembleMotDePasse') : undefined}>
        <Input value={code} onChange={setCode} inputMode="numeric" name="one-time-code"
          autoComplete="one-time-code" required />
      </Field>
      {current.status === 'mfa_required' && current.trustedDeviceDays ? (
        <Switch
          label={t('auth.appareilConfiance', { jours: current.trustedDeviceDays })}
          hint={t('auth.appareilConfianceAide')}
          checked={rememberDevice}
          onChange={setRememberDevice}
        />
      ) : null}
      <Button type="submit" large disabled={busy}>{t('commun.valider')}</Button>
    </form>
  );

  const signIn = (): ReactNode => {
    if (phase === 'compte' && remembered) {
      return (
        <div className="stack">
          <p><strong>{t('auth.bonRetour', { nom: remembered.name || remembered.identifier })}</strong></p>
          <p className="muted">{remembered.identifier}</p>
          {passkeyButton(t('auth.passkeyContinuer', { methode }), () => void onPasskey())}
          <Button variant="secondary" large disabled={passkeyBusy} onClick={() => setPhase('motDePasse')}>
            {t('auth.utiliserMotDePasse')}
          </Button>
          <Button variant="ghost" disabled={passkeyBusy} onClick={oublierCompte}>
            <ArrowLeft size={18} aria-hidden="true" />
            {t('auth.autreCompte')}
          </Button>
        </div>
      );
    }
    if (phase === 'motDePasse') {
      return (
        <form onSubmit={(e) => void onLogin(e)} className="stack">
          {/* L'identifiant reste dans le formulaire, invisible : le gestionnaire de mots de
              passe associe ainsi le mot de passe au bon compte. */}
          <Input value={identifier} onChange={setIdentifier} name="username" autoComplete="username" hidden />
          <RecordList label={t('auth.identifiant')}>
            <li>
              <RecordRow
                onOpen={() => { setPassword(''); setPhase('identifiant'); }}
                lead={<RecordIcon><UserRound size={18} aria-hidden="true" /></RecordIcon>}
                title={identifier}
                next={t('auth.modifierIdentifiant')}
              />
            </li>
          </RecordList>
          <Field label={t('auth.motDePasse')}>
            <Input value={password} onChange={setPassword} type="password" maxLength={PASSWORD_MAX_LENGTH} required
              name="password" autoComplete="current-password" />
          </Field>
          <Button type="submit" large disabled={busy || passkeyBusy}>{t('auth.seConnecter')}</Button>
          {passkeys ? passkeyButton(t('auth.passkey', { methode }), () => void onPasskey(), 'secondary') : null}
          <ButtonLink to="/reinitialisation" variant="ghost">{t('auth.motDePasseOublie')}</ButtonLink>
        </form>
      );
    }
    return (
      <form onSubmit={onIdentifier} className="stack">
        <Field label={t('auth.identifiant')}>
          <Input value={identifier} onChange={setIdentifier} required name="username"
            autoComplete={autofill ? 'username webauthn' : 'username'} />
        </Field>
        <Button type="submit" large disabled={passkeyBusy}>{t('auth.continuer')}</Button>
        {passkeys ? passkeyButton(t('auth.passkey', { methode }), () => void onPasskey(), 'secondary') : null}
      </form>
    );
  };

  const secondStep = (current: Exclude<LoginStep, { status: 'ok' }>): ReactNode => {
    if (current.status === 'invitation') return <Spinner variant="section" />;
    if (current.status === 'choose_company') {
      return (
        <div className="stack">
          <RecordList label={t('compteEntreprises.choisirListe')}>
            {current.choices.map((choice) => (
              <li key={choice.userId}>
                <RecordRow
                  {...(busy ? {} : { onOpen: () => void onChoose(current.accountToken, choice.userId) })}
                  lead={<RecordIcon><ICONS.entreprise size={18} aria-hidden="true" /></RecordIcon>}
                  title={choice.companyName}
                />
              </li>
            ))}
          </RecordList>
          {busy ? <Spinner variant="section" label={t('compteEntreprises.enCours')} /> : null}
          {backButton()}
        </div>
      );
    }
    if (current.status === 'no_company') {
      return (
        <div className="stack">
          {current.invitations.length ? (
            <Section title={t('compteEntreprises.invitations')} flush>
              <RecordList label={t('compteEntreprises.invitations')}>
                {current.invitations.map((invitation) => (
                  <li key={`${invitation.companyName}-${invitation.expiresAt}`}>
                    <RecordRow
                      lead={<RecordIcon><Mail size={18} aria-hidden="true" /></RecordIcon>}
                      title={invitation.companyName}
                      lines={[t('compteEntreprises.invitationLigne', { date: dateLongue(invitation.expiresAt) })]}
                    />
                  </li>
                ))}
              </RecordList>
            </Section>
          ) : null}
          <AdvisorPendingSection companies={current.advisorPending ?? []} accountToken={current.accountToken} />
          {registration?.registrationOpen ? (
            <AccountCompanyForm info={registration} accountToken={current.accountToken}
              onDone={onCompanyCreated} onExpired={(message) => reconnect(message)} />
          ) : null}
          <div className="login-page__links">
            {backButton()}
            <DeleteCompanylessAccount accountToken={current.accountToken} onExpired={(message) => reconnect(message)} />
          </div>
        </div>
      );
    }
    if (emailToken) {
      // Mise en place : le retour ramène au choix de la méthode ; sinon, à l'identifiant.
      const back = current.status === 'mfa_setup_required' ? () => setEmailToken(undefined) : autreCompte;
      return (
        <EmailCodeStep token={emailToken} onBack={back} onStep={setStep}
          trustedDeviceDays={current.status === 'mfa_required' ? current.trustedDeviceDays : undefined} />
      );
    }
    if (current.status === 'passkey_required') {
      const { mfaToken } = current;
      return (
        <div className="stack">
          {passkeyButton(t('auth.passkeyContinuer', { methode }), () => void onPasskey())}
          {current.emailCode && mfaToken ? (
            <Button variant="ghost" disabled={passkeyBusy} onClick={() => setEmailToken(mfaToken)}>
              {t('auth.codeEmail.sansTelephone')}
            </Button>
          ) : null}
          {backButton()}
        </div>
      );
    }
    if (current.status === 'mfa_required') {
      return (
        <div className="stack">
          {totpForm(current)}
          {current.emailCode ? (
            <Button variant="ghost" disabled={busy} onClick={() => setEmailToken(current.mfaToken)}>
              {t('auth.codeEmail.sansTelephone')}
            </Button>
          ) : null}
          {backButton()}
        </div>
      );
    }
    // Mise en place imposée : une méthode mise en avant selon l'appareil (Face ID, Touch ID,
    // empreinte ou Windows Hello s'il en a un, sinon l'appli de codes), les autres en liste.
    const { setupToken } = current;
    if (setupMethod === 'appli') {
      return (
        <div className="stack">
          {totpForm(current)}
          {backButton(() => setSetupMethod('choix'))}
        </div>
      );
    }
    const others = [
      ...(platform ? [{ key: 'appli', title: t('auth.mfaAvecAppli'), line: t('auth.mfaAvecAppliAide'), open: () => chooseApp(setupToken) }] : []),
      ...(!platform && passkeys ? [{ key: 'cle', title: t('auth.mfaCle'), line: t('auth.mfaCleAide'), open: () => void onSetupPasskey(setupToken) }] : []),
      ...(current.emailCode ? [{ key: 'email', title: t('auth.mfaEmail'), line: t('auth.mfaEmailAide'), open: () => setEmailToken(setupToken) }] : []),
    ];
    return (
      <div className="stack">
        {platform ? (
          <>
            {passkeyButton(t('auth.mfaPasskey', { methode }), () => void onSetupPasskey(setupToken))}
            <p className="muted">{t('auth.mfaPasskeyAide')}</p>
          </>
        ) : (
          <Button large disabled={busy || passkeyBusy} onClick={() => chooseApp(setupToken)}>
            <Smartphone size={18} aria-hidden="true" />
            {t('auth.mfaAvecAppli')}
          </Button>
        )}
        {others.length ? (
          <Section title={t('auth.mfaAutres')} flush>
            <RecordList label={t('auth.mfaAutres')}>
              {others.map((method) => (
                <li key={method.key}>
                  <RecordRow onOpen={method.open} lead={<RecordIcon>{method.key === 'email' ? <Mail size={18} aria-hidden="true" />
                    : method.key === 'cle' ? <KeyRound size={18} aria-hidden="true" /> : <Smartphone size={18} aria-hidden="true" />}</RecordIcon>}
                    title={method.title} lines={[method.line]} />
                </li>
              ))}
            </RecordList>
          </Section>
        ) : null}
        {backButton()}
      </div>
    );
  };

  // Titre de l'étape : la phrase d'accueil seulement au premier écran.
  const header = step?.status === 'choose_company'
    ? { title: t('compteEntreprises.choisirTitre'), description: t('compteEntreprises.choisirAide') }
    : step?.status === 'no_company'
      ? { title: t('compteEntreprises.aucuneTitre'), description: t('compteEntreprises.aucuneAide') }
    : step?.status === 'mfa_setup_required'
    ? { title: t('auth.miseEnPlaceTitre'), description: t('auth.miseEnPlaceAide') }
    : step?.status === 'passkey_required'
      ? { title: t('auth.verificationTitre'), description: t('auth.passkeyRequise', { methode }) }
      : !signingIn
        ? { title: t('auth.verificationTitre') }
        : intent === 'inscription'
          ? { title: t('auth.inscriptionTitre'), description: t('auth.accroche') }
          : { title: t('auth.connexion'), ...(phase === 'identifiant' ? { description: t('auth.accroche') } : {}) };

  return (
    <main className={`login-page${backdrop ? ' login-page--backdrop' : ''}`}>
      {backdrop ? <LoginBackdrop /> : null}
      {backupCodes ? (
        <div className="login-page__panel">
          <BackupCodesGate codes={backupCodes} framed={false} onDone={() => {
            const next = afterCodes.current;
            if (!next) return;
            void finishLogin(next).then((following) => {
              setBackupCodes(undefined);
              setStep(following);
            }).catch((err: unknown) => {
              setBackupCodes(undefined);
              setError(err instanceof ApiError ? err.message : t('commun.erreurInattendue'));
            });
          }} />
        </div>
      ) : (
        <div className="login-page__panel anim-page">
          <div className="login-page__brand"><Logo size={38} /></div>
          <PageHeader {...header} />
          <Section flush>
            {/* Les onglets disparaissent pendant le MFA : on ne change pas
                d'intention au milieu d'une authentification en cours. */}
            {signingIn && (registration?.registrationOpen || intent === 'inscription') ? (
              <Tabs value={intent} onChange={setIntent}
                ariaLabel="Se connecter ou créer une entreprise" options={[
                { value: 'connexion' as const, label: t('auth.ongletConnexion') },
                { value: 'inscription' as const, label: t('auth.ongletInscription') },
              ]} />
            ) : null}
            {signingIn && pendingInvitation ? (
              <Banner tone="info" action={{
                label: t('compteEntreprises.plusTard'),
                onClick: () => { writePendingInvitation(undefined); setPendingInvitation(undefined); },
              }}>
                {t('compteEntreprises.bandeauRejoindre', { entreprise: pendingInvitation.companyName })}
              </Banner>
            ) : null}
            {notice ?? returned ? <Banner tone="info">{notice ?? returned}</Banner> : null}
            {error ? <Banner tone="danger">{error}</Banner> : null}
            {signingIn && intent === 'inscription' && registration?.registrationOpen ? (
              <RegisterForm info={registration} onRegistered={connecter} />
            ) : signingIn && intent === 'inscription' ? (
              // Arrivée par un lien alors que l'enregistrement est fermé : le dire.
              // Retomber en silence sur le formulaire de connexion laissait
              // l'utilisateur saisir des identifiants inexistants et lire
              // « identifiant invalide » sans comprendre pourquoi.
              <div className="stack">
                <Banner tone="danger">{t('auth.inscriptionFermee')}</Banner>
                <Button variant="secondary" large onClick={() => setIntent('connexion')}>
                  {t('auth.ongletConnexion')}
                </Button>
              </div>
            ) : signingIn ? signIn() : secondStep(step)}
          </Section>
        </div>
      )}
    </main>
  );
}
